// Interval Training patterns and the home note (issue #3721): the solfège syllables, the short
// patterns a session asks the singer to sing back, the sets they are introduced in, which patterns
// one session plays, and where do sits for a singer's range. Pure functions and plain data shared
// by the exercise state machine, the page and the stories. No DOM, no audio. Tunables (the
// per-level tolerance and hold, patterns per session, sessions per set) live in learn_rules.ts.
//
// The module never names an interval and never names a key (issue #3700): a pattern is "do, mi,
// sol" and do is "the home note". So a pattern here is a list of scale degrees counted from do -
// 0 do, 1 re, 2 mi, 3 fa, 4 sol, 5 la, 6 ti, 7 the do above, -3 the sol below - and everything else
// follows from the degree: its syllable, its pitch once do is chosen, how many lines and spaces it
// sits above do on the staff, and the steps that fill a leap.
//
// Sets. A level's patterns come in sets, in the order every tradition that teaches untrained
// singers introduces them (the sources are on issue #3700): the playground call sol-mi, the home
// chord, then steps. The progress record counts the sessions played on each set, per level
// (LearnModuleProgress.setSessions), and a set is done once its count reaches
// intervalSessionsPerSet. The current set is the first set, in order, that is not done - the one
// Start plays - and every other set not done is still to come; once every set is done there is no
// current set and Start plays a session that mixes them all. A singer may play any set at any time
// from the start screen's checklist (the product owner, 2026-10-02: go back to sol – mi, or jump
// ahead), so a set can be finished out of order: a later set done first shows done, and current
// stays on the first set not done. That is why the count is stored per set rather than read off the
// module's session count.
//
// One session. A session plays one set, or the mix. A set's patterns come first, in order, rising
// before falling, so a session opens by introducing them. After that each pattern is drawn from
// that set with probability intervalCurrentSetShare and from the sets before it in order otherwise,
// whether or not the singer has done them - the order is the teaching order - and never the pattern
// just sung when there is another to pick: the first set, two patterns and nothing to review,
// alternates between them. The mix draws every pattern of the level the same way.
//
// do and the patterns. A pattern plays on its own, its first note first: nothing is played before
// it. Trying the patterns on 2026-10-02 (issue #3721), the product owner found do before them
// distracting - do then sol – mi sounds like a three-note pattern - and most patterns start on do
// anyway. Every pattern is still sung from home, and the house button plays do at any time.
//
// The home note. One do for the whole session, so every pattern in it is in the same key, chosen
// so that do and every note of every pattern sit inside the saved range trimmed by
// rangeTrimSemitones at each end, as Pitch Match trims it. On Easy do is C, F or G: the notes Easy
// sings from those (do, re, mi and sol) are all naturals, so the staff never needs an accidental,
// as in Pitch Match. It moves from one session to the next where the range allows, so nobody
// learns do as one fixed pitch. A range too narrow to trim is used as measured. A range too narrow
// for the session even then is not swapped for the default range, which can be nowhere near the
// singer: do goes where the session reaches out of the range as little as it can (Michael, issue
// #3722: go outside the range only as far as needed), so a session can always be built and stays as
// close to the singer's voice as its patterns allow. Meet the syllables (issue #3720), the screen
// that teaches how to say each syllable, plays its ladder from the lowest of those dos.
//
// Randomness comes from the same RandomSource the Rhythm patterns use, so tests and stories seed
// it and get the same session every time.

import {
  defaultVocalRange,
  intervalCurrentSetShare,
  intervalPatternsPerSession,
  intervalSessionsPerSet,
  rangeTrimSemitones,
} from './learn_rules.ts';
import { LearnLevel, LearnModuleProgress, VocalRange } from './model.ts';
import { RandomSource } from './rhythm_echo.ts';

// ---- Syllables and degrees -------------------------------------------------------------------

export type Solfege = 'do' | 're' | 'mi' | 'fa' | 'sol' | 'la' | 'ti';

// In scale order from do, so a degree indexes it
export const solfegeSyllables: Solfege[] = ['do', 're', 'mi', 'fa', 'sol', 'la', 'ti'];

// How each syllable is said, as Meet the syllables (issue #3720) shows it under the syllable, so
// nobody goes through the module saying "doo"
export const solfegePronunciations: Record<Solfege, string> = {
  do: 'say "doh", rhymes with go',
  re: 'say "ray"',
  mi: 'say "me"',
  fa: 'say "fah"',
  sol: 'say "sole"',
  la: 'say "lah"',
  ti: 'say "tee"',
};

// Semitones above do for each degree of the major scale
const majorScaleSemitones = [0, 2, 4, 5, 7, 9, 11];

const degreesPerOctave = solfegeSyllables.length;

function degreeInOctave(degree: number): number {
  return ((degree % degreesPerOctave) + degreesPerOctave) % degreesPerOctave;
}

export function solfegeFor(degree: number): Solfege {
  return solfegeSyllables[degreeInOctave(degree)];
}

// A note raised or lowered a semitone from its degree of the major scale: Sing in Solfège's minor
// tunes and chromatic notes (issue #3772). Interval Training sings the major scale only, so its
// patterns never alter a note.
export type SolfegeAlteration = -1 | 0 | 1;

// The syllables of the altered notes, in moveable do with do-based minor (the product owner,
// 2026-10-03, issue #3772): do, re, fa, sol and la raised are di, ri, fi, si and li; re, mi, sol,
// la and ti lowered are ra, me, se, le and te. Mi and ti raised, and do and fa lowered, have no
// syllable of their own: those notes are fa, do, ti and mi themselves.
export type ChromaticSolfege = 'di' | 'ri' | 'fi' | 'si' | 'li' | 'ra' | 'me' | 'se' | 'le' | 'te';

// Any syllable a note can be sung on. Interval Training and Meet the syllables use Solfege alone.
export type SolfegeSyllable = Solfege | ChromaticSolfege;

// By degree within the octave, as solfegeSyllables is; undefined where the note has no syllable
const raisedSyllables: (ChromaticSolfege | undefined)[] = ['di', 'ri', undefined, 'fi', 'si', 'li', undefined];
const loweredSyllables: (ChromaticSolfege | undefined)[] = [undefined, 'ra', 'me', undefined, 'se', 'le', 'te'];

// The syllable of a degree raised or lowered: the one function every syllable of a note comes from.
// Throws for mi or ti raised and do or fa lowered, which have none; no phrase may hold such a note,
// and Sing in Solfège checks its phrases as the module loads.
export function solfegeSyllableFor(degree: number, alteration: SolfegeAlteration): SolfegeSyllable {
  if (alteration === 0) {
    return solfegeFor(degree);
  }
  const syllable = (alteration > 0 ? raisedSyllables : loweredSyllables)[degreeInOctave(degree)];
  if (!syllable) {
    throw new Error(`Degree ${degree} ${alteration > 0 ? 'raised' : 'lowered'} has no syllable`);
  }
  return syllable;
}

// How far a degree is from do in semitones, below do when negative
export function semitonesFromDo(degree: number): number {
  return Math.floor(degree / degreesPerOctave) * 12 + majorScaleSemitones[degreeInOctave(degree)];
}

// The note a degree is once do has been chosen
export function intervalNoteMidi(homeMidi: number, degree: number): number {
  return homeMidi + semitonesFromDo(degree);
}

// ---- Patterns and sets -----------------------------------------------------------------------

export interface IntervalPattern {
  degrees: number[]; // the notes in the order they are sung, as scale degrees from do
  label: string; // the plain line under the pattern's syllables; never the name of an interval
}

export interface IntervalPatternSet {
  description: string; // the line under the set's name on the start screen's checklist
  patterns: IntervalPattern[]; // in the order a session introduces them, rising before falling
}

// "do – re – mi", as the screens head a pattern
export function intervalPatternName(pattern: IntervalPattern): string {
  return pattern.degrees.map(solfegeFor).join(' – ');
}

// A set goes by its first pattern on the checklist ("sol – mi", "do – mi – sol")
export function intervalSetName(set: IntervalPatternSet): string {
  return intervalPatternName(set.patterns[0]);
}

const easySets: IntervalPatternSet[] = [
  {
    description: 'The playground call. Easiest skip to sing.',
    patterns: [
      { degrees: [4, 2], label: 'The playground call' },
      { degrees: [4, 2, 4], label: 'The playground call, and back up' },
    ],
  },
  {
    description: 'The home chord. Fills the big jump with steps.',
    patterns: [
      { degrees: [0, 2, 4], label: 'The home chord' },
      { degrees: [0, 4, 0], label: 'The big jump up from home, and back' },
    ],
  },
  {
    description: 'Steps, the way most hymn tunes move.',
    patterns: [
      { degrees: [0, 1, 2], label: 'Steps up from home' },
      { degrees: [2, 1, 0], label: 'Steps down to home' },
    ],
  },
  {
    description: 'Steps up and back down.',
    patterns: [{ degrees: [0, 1, 2, 1, 0], label: 'Steps up from home and back down' }],
  },
];

// Medium and Hard play Easy's sets until issue #3724 gives them their own; only Easy is reachable
// in the app until then.
const patternSets: Record<LearnLevel, IntervalPatternSet[]> = {
  [LearnLevel.Easy]: easySets,
  [LearnLevel.Medium]: easySets,
  [LearnLevel.Hard]: easySets,
};

export function intervalPatternSets(level: LearnLevel): IntervalPatternSet[] {
  return patternSets[level];
}

// ---- Which set a singer is on ----------------------------------------------------------------

// The sessions played on each of the level's sets, in set order, as the progress record holds them:
// undefined before any, and shorter than the sets until a later set is played
export type IntervalSetSessions = number[] | undefined;

export function intervalSetSessions(module: LearnModuleProgress, level: LearnLevel): IntervalSetSessions {
  return module.setSessions?.[level];
}

function isSetDone(setSessions: IntervalSetSessions, index: number): boolean {
  return (setSessions?.[index] ?? 0) >= intervalSessionsPerSet;
}

// The index of the set Start plays, the first set not done, or undefined once every set is done
export function currentIntervalSetIndex(level: LearnLevel, setSessions: IntervalSetSessions): number | undefined {
  const index = intervalPatternSets(level).findIndex((_set, setIndex) => !isSetDone(setSessions, setIndex));
  return index === -1 ? undefined : index;
}

export type IntervalSetStatus = 'done' | 'current' | 'upcoming';

// One status per set of the level, for the start screen's checklist: a set done out of order is
// done, and does not move the current set past an earlier one that is not
export function intervalSetStatuses(level: LearnLevel, setSessions: IntervalSetSessions): IntervalSetStatus[] {
  const current = currentIntervalSetIndex(level, setSessions);
  return intervalPatternSets(level).map((_set, index) => {
    if (isSetDone(setSessions, index)) {
      return 'done';
    }
    return index === current ? 'current' : 'upcoming';
  });
}

// ---- One session's patterns ------------------------------------------------------------------

function pick<T>(candidates: T[], random: RandomSource): T {
  return candidates[Math.min(candidates.length - 1, Math.floor(random() * candidates.length))];
}

// A pattern from `pool` other than the one just sung; from `otherwise` when the pool holds nothing
// else, and the one just sung again only when there is no other pattern at all
function pickAfter(
  pool: IntervalPattern[],
  otherwise: IntervalPattern[],
  previous: IntervalPattern | undefined,
  random: RandomSource,
): IntervalPattern {
  const fresh = pool.filter((pattern) => pattern !== previous);
  if (fresh.length > 0) {
    return pick(fresh, random);
  }
  const others = otherwise.filter((pattern) => pattern !== previous);
  return pick(others.length > 0 ? others : pool, random);
}

// The session's patterns, in order, for a session on the set at `setIndex`, or the mix of every set
// when it is undefined (see the header)
export function pickIntervalPatterns(
  level: LearnLevel,
  setIndex: number | undefined,
  random: RandomSource = Math.random,
  count = intervalPatternsPerSession,
): IntervalPattern[] {
  const sets = intervalPatternSets(level);
  const introduced = setIndex === undefined ? [] : sets[setIndex].patterns;
  // every set for the mix; the sets before this one otherwise
  const review = sets.slice(0, setIndex).flatMap((set) => set.patterns);
  const picked: IntervalPattern[] = [];
  for (let index = 0; index < count; index += 1) {
    if (index < introduced.length) {
      picked.push(introduced[index]);
      continue;
    }
    const previous = picked[picked.length - 1];
    const fromCurrent = introduced.length > 0 && (review.length === 0 || random() < intervalCurrentSetShare);
    picked.push(
      fromCurrent ? pickAfter(introduced, review, previous, random) : pickAfter(review, introduced, previous, random),
    );
  }
  return picked;
}

// ---- The home note ---------------------------------------------------------------------------

// C, F and G: do, re, mi and sol from each are naturals, so Easy never draws an accidental. Every
// level uses them until issue #3724 decides what Medium and Hard, which add fa, la and ti, allow.
const homePitchClasses = [0, 5, 7];

const middleC = 60;

// The lowest and highest note a session sings, in semitones from do
export interface SemitoneSpan {
  low: number;
  high: number;
}

// The lowest and highest of `semitones` from do, with do itself included: the house button plays it
// at any time, so it has to sit in the range even for notes that never sing it. Sing in Solfège
// (issue #3772) gives its phrases' notes by semitones, so a raised or lowered note at either end is
// placed exactly.
export function spanWithDo(semitones: number[]): SemitoneSpan {
  const withDo = [0, ...semitones];
  return { low: Math.min(...withDo), high: Math.max(...withDo) };
}

// The lowest and highest note of the patterns, do included
function semitoneSpan(patterns: IntervalPattern[]): SemitoneSpan {
  return spanWithDo(patterns.flatMap((pattern) => pattern.degrees.map(semitonesFromDo)));
}

function isHomePitchClass(midi: number): boolean {
  return homePitchClasses.includes(((midi % 12) + 12) % 12);
}

function homeNotesWithin(bounds: VocalRange, span: SemitoneSpan): number[] {
  const homes: number[] = [];
  for (let midi = Math.ceil(bounds.lowMidi - span.low); midi + span.high <= bounds.highMidi; midi += 1) {
    if (isHomePitchClass(midi)) {
      homes.push(midi);
    }
  }
  return homes;
}

// Every do that keeps itself and all of `patterns` inside `bounds` exactly as given, ascending: no
// trim and no fallback. Sing in Solfège (issue #3722) asks it whether a hymn phrase fits a singer's
// measured range at all, by the same rule homeNoteCandidates places do with.
export function homeNotesInside(bounds: VocalRange, patterns: IntervalPattern[]): number[] {
  return homeNotesInsideSpan(bounds, semitoneSpan(patterns));
}

// homeNotesInside for notes given by their span from do (spanWithDo)
export function homeNotesInsideSpan(bounds: VocalRange, span: SemitoneSpan): number[] {
  return homeNotesWithin(bounds, span);
}

function trimmed(range: VocalRange): VocalRange {
  return { lowMidi: range.lowMidi + rangeTrimSemitones, highMidi: range.highMidi - rangeTrimSemitones };
}

function overlap(range: VocalRange, limit: VocalRange): VocalRange {
  return { lowMidi: Math.max(range.lowMidi, limit.lowMidi), highMidi: Math.min(range.highMidi, limit.highMidi) };
}

// The range do is placed in. Both pages send a member with no measured range to the Range Finder
// before a session or the ladder is built, so this is the singer's own range; only one that cannot
// be read as a range (an end that is not a number, or the ends the wrong way round) is replaced, by
// the default range, as a member who never ran the Range Finder is everywhere else.
function usableRange(range: VocalRange): VocalRange {
  const { lowMidi, highMidi } = range;
  return Number.isFinite(lowMidi) && Number.isFinite(highMidi) && lowMidi <= highMidi ? range : defaultVocalRange;
}

// How far the session's notes reach out of `bounds` with do on `home`, in semitones: the larger of
// the reach below and above, then the two together
function reachOutside(home: number, span: SemitoneSpan, bounds: VocalRange): [number, number] {
  const below = Math.max(0, bounds.lowMidi - (home + span.low));
  const above = Math.max(0, home + span.high - bounds.highMidi);
  return [Math.max(below, above), below + above];
}

// For a session wider than the range: every do that takes it out of the range as little as can be,
// ascending. The larger reach (below or above) decides, then the reach in all, then, with `within`,
// how far the session strays from the staff; the dos that tie on all of it are returned, so the
// session still moves from one do to the next where two are as close. midi 0 to 127 holds every
// pitch class many times over, so the best do is always among them.
function closestHomeNotes(range: VocalRange, span: SemitoneSpan, within?: VocalRange): number[] {
  let best: number[] = [];
  let bestHomes: number[] = [];
  for (let midi = 0; midi <= 127; midi += 1) {
    if (!isHomePitchClass(midi)) {
      continue;
    }
    const measure = [...reachOutside(midi, span, range), within ? reachOutside(midi, span, within)[0] : 0];
    const order = best.length === 0 ? -1 : compareMeasures(measure, best);
    if (order < 0) {
      best = measure;
      bestHomes = [midi];
    } else if (order === 0) {
      bestHomes.push(midi);
    }
  }
  return bestHomes;
}

function compareMeasures(first: number[], second: number[]): number {
  const index = first.findIndex((value, at) => value !== second[at]);
  return index === -1 ? 0 : first[index] - second[index];
}

// Every do for the whole session, ascending: one set of candidates for all of `patterns`, so every
// pattern of a session is sung from the same do and the singer hears the syllables' relations in
// one key. A do keeps itself and all of `patterns` inside the singer's range trimmed by
// rangeTrimSemitones where it can, inside the range as measured where only that holds the session,
// and where even that is too narrow, as close to the range as the session allows (closestHomeNotes):
// out of it only as far as it must go, never moved to the default range, which can be nowhere near
// the singer. Middle C stands in only if no do were found at all, which cannot happen while there is
// a home pitch class.
//
// `within` is a limit that is not the voice's - the notes on or near the singer's staff, which the
// page passes so the staff stays short (issue #3721) - and so it is never trimmed: the trimming is
// for the ends of a voice, and an end the staff set is not one. A do keeps the session inside both
// where the singer's own range, trimmed or as measured, allows; where it does not, `within` is
// dropped and the singer's range is kept: a session that fits the voice matters more than a short
// staff. Only for a range too narrow for the session does `within` come back, to choose among dos
// that reach out of the range equally far.
export function homeNoteCandidates(range: VocalRange, patterns: IntervalPattern[], within?: VocalRange): number[] {
  return homeNoteCandidatesForSpan(range, semitoneSpan(patterns), within);
}

// homeNoteCandidates for notes given by their span from do (spanWithDo)
export function homeNoteCandidatesForSpan(range: VocalRange, span: SemitoneSpan, within?: VocalRange): number[] {
  const voice = usableRange(range);
  const own = [trimmed(voice), voice];
  const tries = within ? [...own.map((bounds) => overlap(bounds, within)), ...own] : own;
  for (const bounds of tries) {
    const homes = homeNotesWithin(bounds, span);
    if (homes.length > 0) {
      return homes;
    }
  }
  const closest = closestHomeNotes(voice, span, within);
  return closest.length > 0 ? closest : [middleC];
}

// The session's do. `previous` is the session before's, when the page still has it: the next
// session takes another do where the range holds one. `within` as homeNoteCandidates has it.
export function pickHomeNote(
  range: VocalRange,
  patterns: IntervalPattern[],
  random: RandomSource = Math.random,
  previous?: number,
  within?: VocalRange,
): number {
  return pickHomeNoteForSpan(range, semitoneSpan(patterns), random, previous, within);
}

// pickHomeNote for notes given by their span from do (spanWithDo)
export function pickHomeNoteForSpan(
  range: VocalRange,
  span: SemitoneSpan,
  random: RandomSource = Math.random,
  previous?: number,
  within?: VocalRange,
): number {
  const candidates = homeNoteCandidatesForSpan(range, span, within);
  const fresh = candidates.filter((midi) => midi !== previous);
  return pick(fresh.length > 0 ? fresh : candidates, random);
}

// The do that Meet the syllables (issue #3720) plays its ladder from: the lowest home note a
// session of the level could be given for this range. The screen says "the app picks one that fits
// your range", so the ladder's do is one a session would really use, and do through sol sit inside
// the range. The lowest leaves the most room above it for la and ti, which may still fall above a
// narrow range; they are only played on the piano there, never asked of the singer. `within` is the
// limit the page gives a session's do (homeNoteCandidates), so the ladder's do is one a session can
// really be given.
export function ladderHomeNote(range: VocalRange, level: LearnLevel = LearnLevel.Easy, within?: VocalRange): number {
  const patterns = intervalPatternSets(level).flatMap((set) => set.patterns);
  return homeNoteCandidates(range, patterns, within)[0];
}

// ---- Leaps and the steps that fill them ------------------------------------------------------

// Two notes more than a step apart: what "Hear it as steps" fills in
export function isLeap(fromDegree: number, toDegree: number): boolean {
  return Math.abs(toDegree - fromDegree) > 1;
}

// Every degree from one note to the other, both included, a step at a time: do to sol is do, re,
// mi, fa, sol. It can pass through a note no Easy pattern sings (fa), which is the point of it.
export function stepsBetween(fromDegree: number, toDegree: number): number[] {
  const direction = toDegree >= fromDegree ? 1 : -1;
  const steps: number[] = [];
  for (let degree = fromDegree; degree !== toDegree + direction; degree += direction) {
    steps.push(degree);
  }
  return steps;
}
